// @ts-strict-ignore
import React, { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Callout, CopyButton, DataListItem } from '@node-space/ui-components'
import { DepositAccountNumberFormats, Options } from 'types/BankAccountsDetails'
import { Currencies, WalletType } from 'types/types'
import { getLocalBankDetails } from 'utils/wallet'
import useDepositPaymentReference from '../hooks/useDepositPaymentReference'

interface LocalDetailsProps {
  options: Options
  wallet: WalletType
  currency: Currencies
  isSegregated: boolean
}

const LocalDetails = ({ options, wallet, currency, isSegregated }: LocalDetailsProps) => {
  const { t } = useTranslation()
  const accountDetails = getLocalBankDetails(options, wallet, currency, isSegregated, t)
  const paymentReference = useDepositPaymentReference(options)

  const isCubixPaymentMethod = options?.accountNumberFormat === DepositAccountNumberFormats.CUBIX

  const accountDetailsText = useMemo(() => {
    const allBeneficiaryBankDetailsText = Object.keys(accountDetails || {})
      .map(label => `${label}: ${accountDetails[label]}`)
      .join('\n')

    return allBeneficiaryBankDetailsText
  }, [accountDetails])

  if (!Object.keys(accountDetails)?.length) {
    return <Callout variant="error">{t('oopsSomethingWentWrongFetchDespoitDetails')}</Callout>
  }

  return (
    <Box>
      {Object.keys(accountDetails)
        .filter(label => !!label)
        .map(label => {
          return (
            <DataListItem
              key={label}
              body={accountDetails[label]}
              copyText={accountDetails[label]}
              id={label}
              title={label}
            />
          )
        })}
      <Box flexCol justify="center" align="center" paddingT={16}>
        <CopyButton copyText={accountDetailsText}>{t('copyAllDetails')}</CopyButton>
      </Box>

      {!!paymentReference && (
        <Box flexCol gap={16} paddingY={16}>
          <Callout variant="warning">{t('depositInformation')}</Callout>
          {isCubixPaymentMethod && <Callout variant="info">{t('cubixRequestInformation')}</Callout>}
        </Box>
      )}
    </Box>
  )
}

export default memo(LocalDetails)
